import { Toast } from '@components/Toast/Toast';
import { getLibraryUrl } from '@lib/utils/url';
import { Library } from '@stores/Library/Library';
import { LibraryManager } from '@stores/LibraryManager/LibraryManager';
import { NavigateFunction } from 'react-router';
import { toast } from 'sonner';

export const DEFAULT_LIBRARY_NAME = 'My New Library';

/**
 * Shared create-library submit, used by both the desktop `LibraryCreateModal` and the
 * mobile `MobileCreateLibraryDrawer` so their success behaviour can't drift. Creates
 * the library (optionally transcript-only via `skipVisuals`, optionally with custom
 * transcription word hints via `wordHints`), then EITHER hands it back to the caller
 * (`onCreated` — e.g. the Add Media flow preselects it) OR navigates to it with a
 * "Library created" toast.
 *
 * Callers own their own name/skipVisuals/wordHints state, loading flag, and close
 * behaviour; this is only the create + branch that was duplicated between the surfaces.
 */
export async function submitCreateLibrary(params: {
  libraryManager: LibraryManager;
  navigate: NavigateFunction;
  name: string;
  skipVisuals: boolean;
  /** Custom word hints (glossary). Persisted as settings.v1.glossary when non-empty. */
  wordHints?: string[];
  onCreated?: (library: Library) => void;
}): Promise<void> {
  const { libraryManager, navigate, name, skipVisuals, wordHints, onCreated } = params;
  // Translate the UI toggles into the library-settings shape here; createNewLibrary
  // itself stays settings-agnostic.
  const settings: Partial<Library['settings']['v1']> = {};
  if (skipVisuals) settings.skip = { visuals: true };
  if (wordHints && wordHints.length > 0) settings.glossary = wordHints;
  const library = await libraryManager.createNewLibrary(
    name || DEFAULT_LIBRARY_NAME,
    Object.keys(settings).length > 0 ? settings : undefined,
  );
  if (onCreated) {
    onCreated(library);
    return;
  }
  navigate(getLibraryUrl(library._id, library.name));
  toast.custom((t) => <Toast id={t} header="Library created" variant="success" />, {
    duration: 3000,
  });
}
